import { faArrowLeft, faPlus, faRobot, faGear, faSpinnerThird, faTags } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { withIcon } from '../../../core/components/Icon.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page';
import Tooltip from '../../../core/components/Tooltip.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { url } from '../../../core/utils/link.ts';
import { TabFilter } from '../../../document/pages/Overview/internal/FilterBar';
import AgentActivityTab from '../components/AgentActivityTab.tsx';
import CategoriesTab, { CategoriesTabHandle } from '../components/CategoriesTab.tsx';
import SettingsTabPersonal from '../components/SettingsTabPersonal.tsx';
import SettingsTabShared from '../components/SettingsTabShared.tsx';
import { OVERVIEW_PATH, MAILBOX_DETAIL_PATH } from '../constants.ts';
import useMailboxDetail from '../hooks/useMailboxDetail.ts';

const BackIcon = withIcon(faArrowLeft);
const AddIcon = withIcon(faPlus);
const DotIcon = withIcon(faCircle);
const CategoriesIcon = withIcon(faTags);
const AgentActivityIcon = withIcon(faRobot);
const SettingsIcon = withIcon(faGear);
const SpinnerIcon = withIcon(faSpinnerThird);

const TAB_ICONS: Record<string, React.ReactNode> = {
    categories: <CategoriesIcon />,
    activity: <AgentActivityIcon />,
    settings: <SettingsIcon />,
};

const breadcrumbLinkClassName =
    'flex flex-shrink min-w-0 gap-3 rounded transition-colors hover:bg-surface-strong py-1 px-2 -mx-2 items-center cursor-pointer select-text font-semibold text-base text-content-medium hover:text-content-strong';

const SHARED_TABS = ['categories', 'activity', 'settings'] as const;
const PERSONAL_TABS = ['activity', 'settings'] as const;

const MailboxDetailPage = () => {
    const { t } = useTranslation('mailboxes');
    const navigate = useNavigate();
    const { id = '', tab } = useParams();
    const [searchParams] = useSearchParams();
    const type = searchParams.get('type') === 'personal' ? 'personal' : 'shared';

    const { loading, notFound, sharedIntegration, personalIntegration, connectedAgent, refetchShared } =
        useMailboxDetail(id, type);
    const hasCategories = (sharedIntegration?.customCategories?.length ?? 0) > 0;
    const categoriesTabRef = React.useRef<CategoriesTabHandle>(null);

    const tabs = type === 'shared' ? SHARED_TABS : PERSONAL_TABS;
    const activeTab = tabs.includes(tab as any) ? tab : tabs[0];

    const mailInbox = type === 'shared' ? sharedIntegration?.mailInbox : personalIntegration?.mailInbox;
    const enabled = type === 'shared' ? sharedIntegration?.enabled : personalIntegration?.enabled;

    const tabUrl = (nextTab: string) => url(MAILBOX_DETAIL_PATH, { id, tab: nextTab }, { search: { type } });

    const tabsNav = tabs.map((tabKey) => (
        <TabFilter key={tabKey} to={tabUrl(tabKey)} active={activeTab === tabKey}>
            {TAB_ICONS[tabKey]}
            {t(`detail.tabs.${tabKey}`)}
        </TabFilter>
    ));

    if (loading) {
        return (
            <Layout>
                <Page>
                    <Page.Content lowered className="flex items-center justify-center py-16">
                        <SpinnerIcon spin className="text-3xl text-content-weak" />
                    </Page.Content>
                </Page>
            </Layout>
        );
    }

    if (notFound) {
        return (
            <Layout>
                <Page>
                    <Page.Content lowered className="flex flex-col items-center justify-center gap-4 py-16">
                        <span className="text-content-medium text-sm">{t('detail.notFound')}</span>
                        <Link to={OVERVIEW_PATH} className="text-content-brand-strong text-sm font-medium">
                            {t('detail.backToMailboxes')}
                        </Link>
                    </Page.Content>
                </Page>
            </Layout>
        );
    }

    const breadcrumb = (
        <div className="flex items-center">
            <Link to={OVERVIEW_PATH} className={`${breadcrumbLinkClassName} text-content-strong mr-1`}>
                <Tooltip content={t('detail.backToMailboxes')}>
                    <BackIcon />
                </Tooltip>
            </Link>
            <Link to={OVERVIEW_PATH} className={breadcrumbLinkClassName}>
                {t('overview.title')}
            </Link>
            <Page.HeaderSeparator />
            <DotIcon
                className={classnames(
                    'text-[10px] mr-2',
                    enabled ? 'text-content-success-weak' : 'text-content-warning-weak'
                )}
            />
            <Page.HeaderTitle>{mailInbox}</Page.HeaderTitle>
        </div>
    );

    const headerButton = activeTab === 'categories' && hasCategories && (
        <Page.HeaderButton onClick={() => categoriesTabRef.current?.openCreateModal()}>
            <AddIcon />
            {t('detail.categoriesTab.addCategory')}
        </Page.HeaderButton>
    );

    // Settings tab: two stacked Page.Header rows (breadcrumb, then tab nav), matching
    // the structure used by CategoriesTab/AgentActivityTab for identical spacing/borders.
    if (activeTab === 'settings') {
        return (
            <Layout>
                <Page className="flex-1">
                    <Page.Header>{breadcrumb}</Page.Header>
                    <Page.Header className="py-0 justify-between" primary={false}>
                        <div className="flex h-full">{tabsNav}</div>
                    </Page.Header>

                    <Page.Content lowered className="flex flex-col gap-4">
                        {type === 'shared' && sharedIntegration && (
                            <SettingsTabShared
                                integration={sharedIntegration}
                                refetch={refetchShared}
                                onDisconnected={() => navigate(OVERVIEW_PATH)}
                            />
                        )}

                        {type === 'personal' && personalIntegration && (
                            <SettingsTabPersonal
                                integration={personalIntegration}
                                onDisconnected={() => navigate(OVERVIEW_PATH)}
                            />
                        )}
                    </Page.Content>
                </Page>
            </Layout>
        );
    }

    return (
        <Layout>
            <Page className="flex-1">
                <Page.Header className="justify-between">
                    {breadcrumb}
                    {headerButton}
                </Page.Header>

                {activeTab === 'categories' && sharedIntegration && (
                    <CategoriesTab
                        ref={categoriesTabRef}
                        integration={sharedIntegration}
                        connectedAgentName={connectedAgent?.name ?? null}
                        refetch={refetchShared}
                        tabsNav={tabsNav}
                    />
                )}

                {activeTab === 'activity' && (
                    <AgentActivityTab mailboxId={id} connectedAgent={connectedAgent} tabsNav={tabsNav} />
                )}
            </Page>
        </Layout>
    );
};

export default MailboxDetailPage;
